import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { PiX } from 'react-icons/pi';
import { useLocation } from 'wouter';
import Modal from '@/components/modal/Modal';
import { LocalContactPicker } from '@/pages/contacts/LocalContactPicker';
import type { Contact } from '@/models/contacts';
import { useDuplicateOffer } from '@/models/offers';

interface DuplicateOfferModalProps {
  offerId: number;
  offerTitle: string;
  opened: boolean;
  onClose: () => void;
}

export default function DuplicateOfferModal({
  offerId,
  offerTitle,
  opened,
  onClose,
}: DuplicateOfferModalProps) {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yyyy = now.getFullYear();
  const defaultTitle = `${offerTitle} (copy ${dd}.${mm}.${yyyy})`;

  const [title, setTitle] = useState(defaultTitle);
  const [contact, setContact] = useState<Contact | null>(null);
  const duplicateOffer = useDuplicateOffer();
  const [, navigate] = useLocation();

  const handleClear = () => setContact(null);

  const handleDuplicate = () => {
    if (!title.trim() || !contact) return;

    duplicateOffer.mutate(
      { offerId, newContactId: contact.id, newTitle: title.trim() },
      {
        onSuccess: (offer) => {
          navigate(`/${offer.id}`);
          handleClose();
        },
        onError: (e) => {
          notifications.show({
            color: 'red',
            title: 'Error',
            message: `Failed to duplicate offer: ${e}`,
          });
        },
      },
    );
  };

  const handleClose = () => {
    onClose();
    setTitle(defaultTitle);
    setContact(null);
  };

  const contactLabel = contact
    ? [
        [contact.firstName, contact.lastName].filter(Boolean).join(' '),
        contact.email,
      ]
        .filter(Boolean)
        .join(' — ')
    : null;

  return (
    <Modal opened={opened} onClose={handleClose} title="Duplicate Offer">
      <Stack gap="md" p="md">
        <TextInput
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          autoFocus
        />

        {contact ? (
          <Group gap="xs">
            <Text fz="sm" fw={500}>
              {contactLabel}
            </Text>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={handleClear}
            >
              <PiX size={14} />
            </Button>
          </Group>
        ) : (
          <LocalContactPicker
            offerable
            label="Contact"
            placeholder="Search local contacts..."
            onContactSelect={(c) => setContact(c)}
          />
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleDuplicate}
            disabled={!title.trim() || !contact}
            loading={duplicateOffer.isPending}
          >
            Duplicate
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
